import { SettingsCard } from './SettingsCard';
import { SettingsToggle } from './SettingsToggle';
import { settingsPrimaryButtonStyle } from './settingsStyles';
import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
// ----- phase-assistant-model-catalog — Settings → Assistant catalog panel -----
//
// Renders the assistant's model catalog as CONFIGURATION: the models the
// picker and Auto router draw from, each with its tier, per-MTok pricing, and
// enabled flag. Read-only for non-admins; admins can toggle a model's
// `enabled` flag and save (persists to the org `assistantModelCatalog` setting,
// validated server-side). Fable 5 is the advanced tier's preferred/Auto target
// and shows a badge to that effect.

import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type AssistantAdminCatalogModel } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { AssistantConfigNotice } from '@/components/assistant/AssistantConfigNotice';

export interface AssistantModelCatalogPanelProps {
  /** Admins (Users:manage) may edit enabled flags; others see it read-only. */
  readonly canEdit: boolean;
}

const cellStyle: CSSProperties = {
  padding: '12px 10px',
  borderBottom: '1px solid var(--border, #e5e7eb)',
  fontSize: 12,
  textAlign: 'left',
};

export function AssistantModelCatalogPanel({
  canEdit,
}: AssistantModelCatalogPanelProps): ReactElement {
  const [models, setModels] = useState<AssistantAdminCatalogModel[] | null>(null);
  const [isDefault, setIsDefault] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    v2.assistant
      .getAdminModelCatalog()
      .then((res) => {
        if (cancelled) return;
        setModels(res.models);
        setIsDefault(res.isDefault);
      })
      .catch((err: unknown) => {
        // Test #9 — the server's own message (e.g. a configuration error)
        // beats the generic one.
        if (!cancelled) setError(formatApiError(err, 'Could not load the model catalog.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleEnabled = (modelId: string): void => {
    setModels((prev) =>
      prev
        ? prev.map((m) => (m.modelId === modelId ? { ...m, enabled: !m.enabled } : m))
        : prev,
    );
    setDirty(true);
  };

  const save = (): void => {
    if (!models) return;
    setSaving(true);
    setError(null);
    v2.assistant
      .putAdminModelCatalog(models)
      .then((res) => {
        setModels(res.models);
        setIsDefault(false);
        setDirty(false);
      })
      .catch((err: unknown) => setError(formatApiError(err, 'Could not save the catalog. Your changes were not applied.')))
      .finally(() => setSaving(false));
  };

  const pricingSource = models?.find((m) => m.pricingSource)?.pricingSource ?? null;

  return (
    <SettingsCard
      actions={canEdit && (
        <button
          type="button"
          data-testid="catalog-save"
          disabled={!dirty || saving}
          onClick={save}
          style={settingsPrimaryButtonStyle(!dirty || saving)}
        >
          {saving ? 'Saving…' : 'Save catalog'}
        </button>
      )}
      testId="assistant-model-catalog-panel"
      title="Assistant model catalog"
      description={<>
        Choose which models are available to the assistant. Model tiers control automatic selection and usage limits.
        {/* T16-D6 — only claim which catalogue is shown once one has loaded. */}
        {models === null
          ? ''
          : isDefault
            ? ' Showing the shipped default catalog.'
            : ' Customised for this organisation.'}
      </>}
    >

      {/* Test #9 — the catalog is configuration too: say when the assistant
          itself cannot run with it. */}
      <AssistantConfigNotice testId="assistant-catalog-config-notice" />

      {error && (
        <div
          data-testid="catalog-error"
          role="alert"
          style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}
        >
          {error}
        </div>
      )}

      {/* T16-D6 — a failed load shows the error above, never a permanent
          "Loading…". */}
      {models === null ? (
        error ? null : <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <ResponsiveTable style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={cellStyle}>Model</th>
              <th style={cellStyle}>Tier</th>
              <th style={{ ...cellStyle, textAlign: 'right' }}>In $/MTok</th>
              <th style={{ ...cellStyle, textAlign: 'right' }}>Out $/MTok</th>
              <th style={{ ...cellStyle, textAlign: 'center' }}>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.modelId} data-testid={`catalog-row-${m.modelId}`}>
                <td style={cellStyle}>
                  <div style={{ fontWeight: 600 }}>{m.displayName}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{m.modelId}</div>
                  {m.preferredForTier && (
                    <span
                      data-testid={`catalog-preferred-${m.modelId}`}
                      style={{
                        fontSize: 10,
                        color: '#166534',
                        background: '#dcfce7',
                        borderRadius: 4,
                        padding: '0 4px',
                      }}
                    >
                      Auto target ({m.tier})
                    </span>
                  )}
                </td>
                <td style={cellStyle}>{m.tier}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>${m.inPerMTok.toFixed(2)}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>${m.outPerMTok.toFixed(2)}</td>
                <td style={{ ...cellStyle, textAlign: 'center' }}>
                  <SettingsToggle
                    label={`${m.displayName} enabled`}
                    testId={`catalog-enabled-${m.modelId}`}
                    checked={m.enabled}
                    disabled={!canEdit || saving}
                    onChange={() => toggleEnabled(m.modelId)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      {pricingSource && (
        <p
          data-testid="catalog-pricing-source"
          style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}
        >
          Pricing source: {pricingSource}
        </p>
      )}


    </SettingsCard>
  );
}
